// 🚨✨ hey jude — IMPORTANT: I added `OnDestroy` to the core imports so we can properly clean up the search-box subscription (no sneaky memory leaks)
import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit, Pipe, PipeTransform, TemplateRef, ViewChild } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import {
  CellValueChangedEvent,
  ColDef,
  GridApi,
  GridReadyEvent,
  GridSizeChangedEvent,
  ICellRendererParams,
  RowNode,
  ValueFormatterParams,
  ValueGetterParams
} from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { ACCESSORIAL_TYPE } from '../select-options';
// 🚨✨ hey jude — added `takeUntil` so the search-box typing subscription can be cleanly shut down
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';
// 🚨✨ hey jude — added `Subject` + `ReplaySubject` to power the searchable select logic (and keep it reactive)
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';

@Component({
  selector: 'app-new-financials',
  templateUrl: './new-financials.component.html',
  styleUrls: ['./new-financials.component.scss']
})
// 🚨✨ hey jude — added `OnDestroy` here so we can do proper teardown for the search filter subscription
export class NewFinancialsComponent implements OnInit, OnDestroy {
// --- Carrier Confirmation Email stuff ---
@ViewChild('carrierEmailAddressDialog') carrierEmailAddressDialog!: TemplateRef<unknown>;
carrierEmailAddressControl: FormControl = new FormControl('', [Validators.required, Validators.email]);

// NEW: Controls for optional CC/BCC addresses
optionalCCControl: FormControl = new FormControl('');
optionalBCCControl: FormControl = new FormControl('');

sendCopyToggle: boolean = true;
pdfToggle: boolean = false;

// Dropdown "buttons"
ladingFinancialsDropdown: FormControl = new FormControl

  // --- AG Grid instances ---
  private gridApi!: GridApi;

  public ladingCollapsed: boolean[] = [];

  // --- Testing / scaffold data ---
  rowData: LineItem[] = [];
  test_subtotal = 0;
  test_tax = 0;
  test_total = 0;
  pinnedBottomRowData: LineItem[] = [];

  // 🚨✨ hey jude — added these controls to support the searchable select box state (filter input + filtered list + teardown signal)
  public serviceFilterCtrl: FormControl<string | null> = new FormControl<string>('');
  public filteredServiceNames: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
  protected _onDestroy = new Subject<void>();

  getPinnedBottomRowData(): LineItem[] {
    return [
      {
        service: 'Subtotal',
        rate: this.test_subtotal,
        multiplier: 1,
        net_amount: this.test_subtotal,
        note: '',
        freight_rate_uuid: null,
        contact_service_rate_uuid: null,
        charge_mode: "summary"
      },
      {
        service: 'Tax',
        rate: this.test_subtotal,
        multiplier: this.test_tax,
        net_amount: +(this.test_subtotal * this.test_tax).toFixed(2),
        note: '',
        freight_rate_uuid: null,
        contact_service_rate_uuid: null,
        charge_mode: "tax"
      },
      {
        service: 'Total',
        rate: this.test_total,
        multiplier: 1,
        net_amount: this.test_total,
        note: '',
        freight_rate_uuid: null,
        contact_service_rate_uuid: null,
        charge_mode: "summary"
      }
    ];
  }

  // --- Column definitions ---
  financialsColumnDefs: ColDef[] = [
    {
      field: 'service',
      headerName: 'Service',
      flex: 2,
      minWidth: 120
    },
    {
      field: 'rate',
      headerName: 'Rate',
      type: 'numericColumn',
      flex: 1,
      minWidth: 80,
      editable: true,
      valueFormatter: (p: ValueFormatterParams) =>
        p.value == null ? '' : `$${(+p.value).toFixed(2)}`
    },
    {
      field: 'multiplier',
      headerName: 'Multiplier',
      type: 'numericColumn',
      flex: 1,
      minWidth: 80,
      editable: true
    },
    {
      field: 'net_amount',
      headerName: 'Net Amount',
      flex: 1,
      minWidth: 100,
      valueGetter: this.netAmountGetter.bind(this),
      valueFormatter: (p: ValueFormatterParams) =>
        p.value == null ? '' : `$${p.value.toFixed(2)}`
    },
    {
      field: 'note',
      headerName: 'Note',
      flex: 3,
      minWidth: 150,
      editable: true
    },
    {
      field: 'actions',
      headerName: 'Actions',
      maxWidth: 120,
      pinned: 'right',
      cellRenderer: (params: ICellRendererParams) => {
        if ((params.node as RowNode).rowPinned) return '';
        return `<button class="ag-btn-edit" title="Edit"><i class="fas fa-edit"></i></button>
                <button class="ag-btn-delete" title="Delete"><i class="fas fa-trash"></i></button>`;
      },
      onCellClicked: ev => {
        const me = ev.event as MouseEvent;
        if ((me.target as HTMLElement).closest('.ag-btn-delete')) {
          this.deleteCharge(ev.data);
        }
        if ((me.target as HTMLElement).closest('.ag-btn-edit')) {
          this.editCharge(ev.data);
        }
      }
    },
    { field: 'freight_rate_uuid', hide: true },
    { field: 'contact_service_rate_uuid', hide: true }
  ];
  raterColumnDefs: ColDef[] = this.financialsColumnDefs.filter(col => col.field !== 'actions');

  // --- Inputs & flags ---
  @Input() hideMenu = false;
  is_labour_breakdown_collapsed = true;
  loading_autorater_results = false;
  lading_uuid: string | null = null;
  edi_customer = false;
  is_technogym = false;
  lading_financials_list: LadingData[] = [];
  autorater_needed = true;
  autorated_ladings_list: LadingData[] = [];
  original_autorated_ladings_list: LadingData[] = [];
  autorated_cost_net_amounts: any;
  autorated_chargeable_net_amounts: any;

  missing_data = false;
  missing_data_form: FormArray = this.fb.array([]);
  filtered_contacts_list: Observable<any[]> = of([]);

  master_financials: LadingData | null = null;
  master_exists = false;
  master_card_collapsed = false;

  // --- Forms & dialogs ---
  autoraterForm: FormGroup;
  accessorials = ACCESSORIAL_TYPE;
  displayedColumns = ['service','rate','multiplier','net_amount','note'];
  autoraterDisplayedColumns = ['service','rate','multiplier','total'];
  SERVICE_RULES: Record<string, { rate: boolean; multiplier: boolean }> = {
    'Freight': { rate: true, multiplier: false },
    'FSC': { rate: false, multiplier: true },
    'Taxes': { rate: false, multiplier: true },
    'Discount': { rate: true, multiplier: true },
    'Handling': { rate: true, multiplier: false },
    'Installation': { rate: true, multiplier: false }
  };

  showCarrierSpotQuoteModal: boolean = false;
  openCarrierSpotQuoteModal(){
    this.showCarrierSpotQuoteModal = true;
  }
  closeCarrierSpotQuoteModal(){
    this.showCarrierSpotQuoteModal = false;
  }

  // Edit existing
  isEditLineChargeOpen = false;
  currentRow: any = null;
  editLineChargeForm: FormGroup = this.fb.group({
    service: new FormControl({ value: '', disabled: true }),
    rate: [0.0],
    rate_multiplier: [0.0],
    net_amount: new FormControl({ value: 0.0, disabled: true }),
    lading_uuid: [''],
    note: ['']
  });

  // Add new
  isAddServiceOpen = false;
  addServiceForm: FormGroup = this.fb.group({
    service: ['', Validators.required],
    rate: [0.0, Validators.required],
    multiplier: [1.0, Validators.required],
    net_amount: new FormControl({ value: 0.0, disabled: true }),
    charge_type: [-1, Validators.required],
    contact_service_rate_uuid: [''],
    note: ['']
  });
  chargeTypes = ['cost', 'chargeable'];
  serviceNames: string[] = [];

  // Technogym breakdown
  productBreakdown: ProductBreakdownRow[] = [];
  productBreakdownDisplayedColumns = [
    'product_description','product_class','installation_min','handling_min',
    'installation_cost','installation_chargeable','handling_cost','handling_chargeable'
  ];
  totalMinutes: TotalMinutes = {};

  // URLs
  autorateURL = '/api/work_orders/autorater.php';
  financialsURL = '/api/work_orders/new_lading_financials.php?lading_uuid=';
  contactProfilesURL = '/api/contacts/contact_profiles.php?contact_type=customer&lading_uuid=';

  // Contact Profiles for autorater options
  contactProfiles: ContactProfile[] = [{profile_name: 'Default'}]

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private ar: ActivatedRoute,
    private toastr: ToastrService,
    private dialog: MatDialog
  ) {
    this.autoraterForm = this.fb.group({
      additionalAccessorials: this.fb.array(this.accessorials.map(() => false))
    });

    // Auto-calc in dialogs
    this.editLineChargeForm.get('rate')!.valueChanges.subscribe(() => this.calculateNetAmount('edit'));
    this.editLineChargeForm.get('rate_multiplier')!.valueChanges.subscribe(() => this.calculateNetAmount('edit'));
    this.addServiceForm.get('rate')!.valueChanges.subscribe(() => this.calculateNetAmount('add'));
    this.addServiceForm.get('multiplier')!.valueChanges.subscribe(() => this.calculateNetAmount('add'));
  }

  ngOnInit(): void {
    // grab UUID & load
    this.ar.paramMap.subscribe(p => this.lading_uuid = p.get('uuid'));
    this.reloadLadingFinancials();

    // EDI & tech breakdown
    if (this.lading_uuid) {
      this.http.get<{ file:string, code:string, message:boolean|string }>(
        `/api/work_orders/check_if_edi_customer.php?lading_uuid=${this.lading_uuid}`
      ).subscribe(r => {
        this.edi_customer = r.code==='200' && typeof r.message==='boolean' ? r.message : false;
      });
      this.http.get<{ product_breakdown:ProductBreakdownRow[], total_minutes:TotalMinutes }|ErrorResponse>(
        `/api/work_orders/technogym_labour.php?lading_uuid=${this.lading_uuid}`
      ).subscribe(r => {
        if ('product_breakdown' in r) {
          this.is_technogym = true;
          this.productBreakdown = r.product_breakdown;
          this.totalMinutes = r.total_minutes;
        }
      });
    }

    // Contact profiles for autorater options
    this.loadContactProfiles();

    // 🚨✨ hey jude — added this subscription so the search box actually reacts to typing; wired through `takeUntil` so it won’t leak when the component dies
    this.serviceFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterServices();
      });
  }

  // 🚨✨ hey jude — cleanup: this is the “no leaks, no regrets” teardown for the search subscription
  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  private loadContactProfiles(): void {
    if (!this.lading_uuid) return;

    this.http.get<ContactProfilesResponse>(this.contactProfilesURL + this.lading_uuid).subscribe(
      res => {
        if(res['code'] == 200 && res['data']){
          if(res['data'][0].hasOwnProperty('profile_name') && res['data'][0].profile_name === null){
            res['data'][0].profile_name = 'Use Customer Profile';
          }
          this.contactProfiles = [...this.contactProfiles, ...res['data']];
        }
        else {
          this.toastr.error('Error loading contact profiles for autorater');
          console.error(res);
        }
      },
      err => {
        this.toastr.error('Error loading contact profiles for autorater');
        console.error(err);
      }
    )
  }

  runAutoraterV2(contact_profile_uuid?: string){
    console.log(`run autorater button clicked with contact profile ${contact_profile_uuid}`);
    // note: if no profile uuid, it is undefined

    if (!this.lading_uuid) return;
    this.loading_autorater_results = true;

    // Assemble post body 
    const body: SemiautoraterPostBody = {
      lading_uuid: this.lading_uuid,
      action: 'DISPLAY',
      data: {}
    }
    if (typeof contact_profile_uuid !== 'undefined'){
      body['contact_profile_uuid'] = contact_profile_uuid;
    }

    console.log('post body:', JSON.stringify(body));

    // Run the [semi-]autorater
    this.http.post<any>(this.autorateURL, body).subscribe(
      resp => {
        console.log(resp)
        this.loading_autorater_results = false;
        if (resp.code==='202') {
          this.toastr.error('Please double check product weights');
          return;
        }
        if (resp.code==='422') {
          this.toastr.error('Missing data');
          if (resp.data && !this.missing_data_form.length) {
            this.createMissingDataForm(resp.data);
            this.missing_data = true;
          }
          return;
        }
        if ('code' in resp && resp.code !== '200'){
          this.toastr.error('Calculation failed');
          return;
        }
        if (!Array.isArray(resp)) {
          this.toastr.error('Calculation failed');
          return;
        }
        this.toastr.success('Calculation successful');
        this.autorated_ladings_list = [];
        this.original_autorated_ladings_list = [];
        for (const l of resp) {
          if (l.lading_uuid && l.alternate_reference && l.costs && l.chargeables) {
            const formatted: LadingData = {
              lading_uuid: l.lading_uuid,
              alternate_reference: l.alternate_reference,
              financials: { costs: l.costs, chargeables: l.chargeables },
              warnings: l.warnings || []
            };
            this.autorated_ladings_list.push(formatted);
            this.original_autorated_ladings_list.push(structuredClone(formatted));
          }
        }
      },
      err => {
        this.loading_autorater_results = false;
        console.error(err);
        this.toastr.error('Error retrieving calculations');
      }
    )
  }

  /** Reload & normalize new-API response */
  private reloadLadingFinancials(): void {
    if (!this.lading_uuid) return;

    this.http.get<any[]>(this.financialsURL + this.lading_uuid).subscribe(
      resp => {
        if (!Array.isArray(resp)) return;
        this.master_financials = null;
        resp.forEach(raw => {
          if (raw.lading_type === 10) {
            this.master_financials = this.normalizeLading(raw);
            this.master_exists = true;
          }
        });
        if (!this.master_financials && resp.length) {
          this.master_financials = this.normalizeLading(resp[0]);
          this.master_exists = false;
        }
        if (this.master_financials) {
        const costsLen      = this.master_financials.financials.costs.line_items.length;
        const chargeablesLen = this.master_financials.financials.chargeables.line_items.length;
        // if either has existing items, we don’t need the autorater
        this.autorater_needed = !(costsLen > 0 || chargeablesLen > 0);
      }
        if (this.master_financials) {
          const c = this.master_financials.financials.costs;
          this.rowData = c.line_items;
          this.test_subtotal = c.subtotal;
          this.test_tax      = c.tax;
          this.test_total    = c.total;
          this.pinnedBottomRowData = this.getPinnedBottomRowData();
        }
        // this.lading_financials_list = resp
        //   .filter(r => r.lading_type !== 10)
        //   .map(r => this.normalizeLading(r));
        this.lading_financials_list = resp
        .map(raw => this.normalizeLading(raw));

        this.ladingCollapsed = this.lading_financials_list.map(_ => false);

      },
      err => console.error(err)
    );
  }

  /** Turn raw JSON into LadingData */
  private normalizeLading(raw: any): LadingData {
    const costsArr = Array.isArray(raw.costs) ? raw.costs : [];
    const billArr  = Array.isArray(raw.billables) ? raw.billables : [];
    const t        = raw.totals || {};
    return {
      lading_uuid: raw.lading_uuid,
      alternate_reference: raw.alternate_reference,
      lading_type: raw.lading_type,
      financials: {
        costs: {
          line_items: costsArr.map((it: any) => ({
            service: it.service,
            rate: parseFloat(it.rate),
            multiplier: parseFloat(it.multiplier),
            net_amount: parseFloat(it.total),
            freight_rate_uuid: null,
            contact_service_rate_uuid: null,
            note: it.note,
            lading_charge_line_item_uuid: it.lading_charge_line_item_uuid
          })),
          subtotal: t.subtotal_cost,
          tax: t.tax_cost,
          total: t.total_cost
        },
        chargeables: {
          line_items: billArr.map((it: any) => ({
            service: it.service,
            rate: parseFloat(it.rate),
            multiplier: parseFloat(it.multiplier),
            net_amount: parseFloat(it.total),
            freight_rate_uuid: null,
            contact_service_rate_uuid: null,
            note: it.note,
            lading_charge_line_item_uuid: it.lading_charge_line_item_uuid
          })),
          subtotal: t.subtotal_billable,
          tax: t.tax_billable,
          total: t.total_billable
        }
      }
    };
  }

  // — Grid lifecycle + persistence
  onGridReady(ev: GridReadyEvent): void {
    this.gridApi = ev.api;
    //this.gridColumnApi = ev.columnApi;
    //consolidationDefaultGridState.restoreState(this.gridApi);
    this.gridApi.sizeColumnsToFit();
  }

  onGridSizeChanged(ev: GridSizeChangedEvent): void {
    this.gridApi.sizeColumnsToFit();
    //consolidationDefaultGridState.saveState(this.gridApi);
  }

  // — In-grid edits
  onCostCellValueChanged(ev: CellValueChangedEvent): void {
    const it = ev.data as LineItem;
    it.net_amount = (it.rate ?? 0) * (it.multiplier ?? 1);
    this.test_subtotal = this.rowData.reduce((sum, x) => sum + (x.net_amount||0), 0);
    this.test_total    = this.test_subtotal + this.test_tax;
    this.pinnedBottomRowData = this.getPinnedBottomRowData();
  }


  private netAmountGetter(params: ValueGetterParams): number {
    const r = parseFloat(params.data?.rate as any) || 0;
    const m = parseFloat(params.data?.multiplier as any) || 0;
    return r * m;
  }

  // — Template mat-table delete
  deleteCharge(row: LineItem): void {
    const uuid = row.lading_charge_line_item_uuid;
    if (!uuid) return;

    const params = new HttpParams().set('uuid', uuid);
    this.http
      .delete('/api/work_orders/edit_line_item.php', { params })
      .subscribe({
        next: () => {
          this.toastr.success('Charge deleted');
          // re-fetch everything:
          this.reloadLadingFinancials();

        },
        error: () => this.toastr.error('Error deleting charge')
      });
  }
  financialsDisplayedColumns: string[] = [
    'service','rate','multiplier','net_amount','note','actions'
  ];

  // — Edit existing
  editCharge(row: any): void {
    this.isEditLineChargeOpen = true;
    this.currentRow = row;
    this.editLineChargeForm.patchValue({
      service: row.service,
      rate: row.rate,
      rate_multiplier: row.multiplier,
      net_amount: row.net_amount,
      note: row.note
    });
  }
  closeEditCharge(): void {
    this.isEditLineChargeOpen = false;
    this.editLineChargeForm.reset();
    this.currentRow = null;
  }
  calculateNetAmount(form: 'edit'|'add'): void {
    const f = form === 'edit' ? this.editLineChargeForm : this.addServiceForm;
    const rate = +f.get('rate')!.value || 0;
    const mult = +f.get(form==='edit'?'rate_multiplier':'multiplier')!.value || 0;
    f.get('net_amount')!.setValue(rate * mult, { emitEvent: false });
  }
  saveCharge(): void {
    if (!this.currentRow?.lading_charge_line_item_uuid) return;
    const body = {
      rate: this.editLineChargeForm.value.rate * 100,
      rate_multiplier: this.editLineChargeForm.value.rate_multiplier,
      note: this.editLineChargeForm.value.note,
      lading_uuid: this.lading_uuid,
      lading_charge_uuid: this.currentRow.lading_charge_line_item_uuid,
      action: 'edit_line_item'
    };
    const params = new HttpParams().set('uuid', this.currentRow.lading_charge_line_item_uuid);
    this.http.post(`/api/work_orders/edit_line_item.php`, body, { params }).subscribe(
      () => this.toastr.success('Line item updated'),
      () => this.toastr.error('Update failed')
    ).add(() => {
      this.closeEditCharge();
      this.reloadLadingFinancials();
    });
  }

  // — Add new
  addService(chargeType?: number, serviceName?: string): void { 
    this.isAddServiceOpen = true; 
    if(typeof chargeType !== 'undefined'){
      this.addServiceForm.get('charge_type')?.setValue(chargeType);
      this.onRateTypeSelected(chargeType);
    }
    if(typeof serviceName !== 'undefined'){
      this.addServiceForm.get('service')?.setValue(serviceName);
    }
  }
  closeAddService(): void {
    this.isAddServiceOpen = false;
    this.addServiceForm.reset();
  }
  onSelectRateType(ev: MatSelectChange): void {
    this.onRateTypeSelected(ev.value)
  }
  onRateTypeSelected(rateType: number, serviceName?: string): void{
    if (!this.lading_uuid) return;
    let url = `/api/rates/service_names.php?lading_uuid=${this.lading_uuid}`;
    url += rateType === 1 ? '&contact_type=carrier' : '&contact_type=customer';
    this.http.get<any>(url).subscribe(r => {
      if (r.code==='200' && r.message){
        this.serviceNames = r.message;
        
        // 🚨✨ hey jude — pushing the initial service list into the `ReplaySubject` so the dropdown has data BEFORE any typing happens
        this.filteredServiceNames.next(this.serviceNames.slice());

        console.log(r);
        console.log(url);

        if (typeof serviceName !== 'undefined' && this.serviceNames.includes(serviceName)){
          this.addServiceForm.get('service')?.setValue(serviceName);
        }
      } 
      else console.log(r);
    });

  }

  // 🚨✨ hey jude — added this whole method: it’s the actual filter engine for the searchable dropdown (handles empty search + case-insensitive matching)
  protected filterServices() {
    if (!this.serviceNames) {
      return;
    }
    let search = this.serviceFilterCtrl.value;
    if (!search) {
      this.filteredServiceNames.next(this.serviceNames.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredServiceNames.next(
      this.serviceNames.filter(service => service.toLowerCase().indexOf(search!) > -1)
    );
  }

  checkForExistingRate(): void {
    if (this.addServiceForm.invalid) {
      this.toastr.error('Charge type & service required');
      return;
    }
    const ct  = this.addServiceForm.value.charge_type;
    const svc = encodeURIComponent(this.addServiceForm.value.service);
    const url = `/api/work_orders/accessorial_service_rates.php?lading_uuid=${this.lading_uuid}` +
                `&charge_type=${ct}&service=${svc}&action=check_service_rate`;
    this.http.get<any>(url).subscribe(r => {
      if (r.rate) {
        this.addServiceForm.get('rate')!.setValue(r.rate);
        this.toastr.success('Existing rate found');
        if (r.contact_service_rate_uuid) {
          this.addServiceForm.get('contact_service_rate_uuid')!.setValue(r.contact_service_rate_uuid);
        }
      } else {
        this.toastr.error('No existing rate');
      }
    });
  }
  saveNewService(): void {
    if (this.addServiceForm.invalid) {
      this.toastr.error('Missing form data');
      return;
    }
    const body = { ...this.addServiceForm.value, lading_uuid: this.lading_uuid };
    console.log(body);
    this.http.post<{ code: string }>(`/api/work_orders/accessorial_service_rates.php`, body).subscribe(
      r => {
        if (r.code==='200') {
          this.toastr.success('Charge added');
          this.reloadLadingFinancials();
          this.closeAddService();
        } else {
          this.toastr.error('Add failed');
        }
      },
      () => this.toastr.error('Add error')
    );
  }

  // — Send EDI
  sendEDIFile(): void {
    if (!this.lading_uuid) return;
    this.http.get(`/api/work_orders/create_EDI_file_the_correct_way.php?lading_uuid=${this.lading_uuid}`)
      .subscribe(() => this.toastr.success('EDI sent'), () => this.toastr.error('EDI error'));
  }

  // — Autorater & missing-data
  runAutorater(premium_rate?: string): void {
    if (!this.lading_uuid) return;
    this.loading_autorater_results = true;
    const body = {
      lading_uuid: this.lading_uuid,
      action: 'DISPLAY',
      data: {},
      premium_rate: premium_rate ?? null
    };
    this.http.post<any>(this.autorateURL, body).subscribe(
      resp => {
        console.log(resp)
        this.loading_autorater_results = false;
        if (resp.code==='202') {
          this.toastr.error('Please double check product weights');
          return;
        }
        if (resp.code==='422') {
          this.toastr.error('Missing data');
          if (resp.data && !this.missing_data_form.length) {
            this.createMissingDataForm(resp.data);
            this.missing_data = true;
          }
          return;
        }
        if ('code' in resp && resp.code !== '200'){
          this.toastr.error('Calculation failed');
          return;
        }
        if (!Array.isArray(resp)) {
          this.toastr.error('Calculation failed');
          return;
        }
        this.toastr.success('Calculation successful');
        this.autorated_ladings_list = [];
        this.original_autorated_ladings_list = [];
        for (const l of resp) {
          if (l.lading_uuid && l.alternate_reference && l.costs && l.chargeables) {
            const formatted: LadingData = {
              lading_uuid: l.lading_uuid,
              alternate_reference: l.alternate_reference,
              financials: { costs: l.costs, chargeables: l.chargeables },
              warnings: l.warnings || []
            };
            this.autorated_ladings_list.push(formatted);
            this.original_autorated_ladings_list.push(structuredClone(formatted));
          }
        }
      },
      err => {
        this.loading_autorater_results = false;
        console.error(err);
        this.toastr.error('Error retrieving calculations');
      }
    );
  }

  onEditAutoraterCharge(element: any): void {
    console.log('Edit autorater row', element);
  }
  // updateAutoraterNetAmount(item: LineItem): void {
  //   if (item.rate != null) {
  //     item.net_amount = item.rate * (item.multiplier ?? 1);
  //   }
  //   else{
  //     item.net_amount = 0;
  //   }
  // }
  updateAutoraterRate(item: LineItem): void {
    item.rate = item.net_amount ?? 0;
    item.multiplier = 1;
  }
  calculateAutoraterSubtotal(i: number, which: number): number {
    const list = which === 1
      ? this.autorated_ladings_list[i].financials.costs.line_items
      : this.autorated_ladings_list[i].financials.chargeables.line_items;
    return list.reduce((sum, it) => sum + (it.net_amount || 0), 0);
  }
  postAutoraterCharges(): void {
    if (!this.autorated_ladings_list.length) return;
    for (const l of this.autorated_ladings_list) {
      for (const c of [...l.financials.costs.line_items, ...l.financials.chargeables.line_items]) {
        if (c.rate == null || c.multiplier == null) {
          this.toastr.error(`Missing rate/multiplier for ${l.alternate_reference}`);
          return;
        }
      }
    }

    console.log(JSON.stringify(this.autorated_ladings_list))
    this.http.post('/api/work_orders/new_lading_financials.php', this.autorated_ladings_list)
      .subscribe(
        (response) => {
          console.log(response)
          this.toastr.success('Charges saved');
        },
        (error) => {
          this.toastr.error('Error saving autorater charges')
        }
      );
  }

  setupContactAutocomplete(i: number, name: string, type: string): void {
    this.missing_data_form.at(i).get(name)!.valueChanges.pipe(
      startWith(''), switchMap(val =>
        typeof val==='string' && val.length>=3
          ? this.getContactsList(val, type)
          : of([])
      )
    ).subscribe(list => this.filtered_contacts_list = of(list));
  }
  getContactsList(query: string, contactType: string): Observable<any[]> {
    let url = `/api/work_orders/contact_autofill.php?search=${encodeURIComponent(query)}`;
    switch (contactType) {
      case 'carrier':   url += '&type=carrier_contact_list';  break;
      case 'customer':  url += '&type=customer_contact_list'; break;
      case 'shipper':   url += '&type=shipper_contact_list';  break;
      case 'consignee': url += '&type=consignee_contact_list';break;
    }
    return this.http.get<any[]>(url).pipe(map(r => r||[]));
  }
  onContactSelected(c: any, i: number, name: string): void {
    this.missing_data_form.at(i).get(name)!.setValue(c.value);
  }
  createMissingDataForm(data: any[]): void {
    data.forEach((l, i) => {
      const fg = this.fb.group({});
      fg.addControl('lading_uuid', new FormControl(l.lading_uuid));
      fg.addControl('alternate_reference', new FormControl(l.alternate_reference));
      const fields: string[] = l.missing_data;
      if (fields.includes('carrier_contact_uuid')) {
        fg.addControl('carrier_name_query', new FormControl(''));
        fg.addControl('carrier_contact_uuid', new FormControl('', Validators.required));
      }
      if (fields.includes('customer_contact_uuid')) {
        fg.addControl('customer_name_query', new FormControl(''));
        fg.addControl('customer_contact_uuid', new FormControl('', Validators.required));
      }
      if (fields.includes('shipper_contact_uuid')) {
        fg.addControl('shipper_name_query', new FormControl(''));
        fg.addControl('shipper_contact_uuid', new FormControl('', Validators.required));
      }
      if (fields.includes('consignee_contact_uuid')) {
        fg.addControl('consignee_name_query', new FormControl(''));
        fg.addControl('consignee_contact_uuid', new FormControl('', Validators.required));
      }
      if (fields.includes('load_weight')) {
        fg.addControl('load_weight', new FormControl(0.0, Validators.required));
      }
      if (fields.includes('load_start')) {
        fg.addControl('load_start', new FormControl(new Date(), Validators.required));
      }
      if (fields.includes('shipper_locality')) {
        fg.addControl('shipper_locality', new FormControl('', Validators.required));
      }
      if (fields.includes('shipper_region')) {
        fg.addControl('shipper_region', new FormControl('', Validators.required));
      }
      if (fields.includes('consignee_locality')) {
        fg.addControl('consignee_locality', new FormControl('', Validators.required));
      }
      if (fields.includes('consignee_region')) {
        fg.addControl('consignee_region', new FormControl('', Validators.required));
      }
      this.missing_data_form.push(fg);
      this.setupContactAutocomplete(i, 'carrier_name_query', 'carrier');
      this.setupContactAutocomplete(i, 'customer_name_query', 'customer');
      this.setupContactAutocomplete(i, 'shipper_name_query', 'shipper');
      this.setupContactAutocomplete(i, 'consignee_name_query', 'consignee');
    });
  }
  displayFn(contact: any): string {
    return contact?.label || '';
  }
  get missingDataControls(): FormGroup[] {
    return this.missing_data_form.controls as FormGroup[];
  }
  onSubmitMissingData(): void {
    const allFilled = this.missing_data_form.controls.every(fg => {
      const g = fg as FormGroup;
      return Object.entries(g.controls)
        .filter(([k, ctl]) => ctl.hasValidator(Validators.required))
        .every(([k, ctl]) => ctl.value !== null && ctl.value !== '');
    });
    if (!allFilled) {
      this.toastr.error('All fields must be filled');
      return;
    }
    const postBody: MissingDataPostBody = {};
    this.missing_data_form.controls.forEach(fg => {
      const g = fg as FormGroup;
      const uuid = g.get('lading_uuid')!.value;
      postBody[uuid] = {};
      Object.keys(g.controls).forEach(key => {
        if (![
          'lading_uuid','alternate_reference',
          'carrier_name_query','customer_name_query',
          'shipper_name_query','consignee_name_query'
        ].includes(key)) {
          postBody[uuid][key] = g.get(key)!.value;
        }
      });
    });
    this.http.post<any>(this.autorateURL, {
      lading_uuid: this.lading_uuid,
      data: postBody,
      action: 'DISPLAY'
    }).subscribe(
      () => this.toastr.success('Re-calculation done'),
      () => this.toastr.error('Error recalculating')
    );
  }


getPinnedBottomRowDataFor(l: LadingData, which: 'costs'|'chargeables'): LineItem[] {
  const block = which === 'costs'
    ? l.financials.costs
    : l.financials.chargeables;

  return [
    {
      service: 'Subtotal',
      rate: block.subtotal,
      multiplier: 1,
      net_amount: block.subtotal,
      freight_rate_uuid: null,
      contact_service_rate_uuid: null,
      note: null,
      charge_mode: "summary"
    },
    {
      service: 'Tax',
      rate: block.subtotal,
      multiplier: block.tax,
      net_amount: +(block.subtotal * block.tax).toFixed(2),
      freight_rate_uuid: null,
      contact_service_rate_uuid: null,
      note: null,
      charge_mode: "tax"
    },
    {
      service: 'Total',
      rate: block.total,
      multiplier: 1,
      net_amount: block.total,
      freight_rate_uuid: null,
      contact_service_rate_uuid: null,
      note: null,
      charge_mode: "summary"
    }
  ];
}

onCellValueChanged(event: CellValueChangedEvent, ladingIndex: number, lading: LadingData, which: 'costs'|'chargeables'){
  const rowIndex = event['node']['rowIndex'];
  if (rowIndex === null) return; //rowIndex would be null for pinned rows

  // Update net amount and refresh
  const rate = this.autorated_ladings_list[ladingIndex]['financials'][which]['line_items'][rowIndex]['rate'] ?? 0;
  if (this.autorated_ladings_list[ladingIndex]['financials'][which]['line_items'][rowIndex]['multiplier'] === null){
    this.autorated_ladings_list[ladingIndex]['financials'][which]['line_items'][rowIndex]['multiplier'] = 1;
  }
  const multiplier = this.autorated_ladings_list[ladingIndex]['financials'][which]['line_items'][rowIndex]['multiplier'] ?? 1;
  this.autorated_ladings_list[ladingIndex]['financials'][which]['line_items'][rowIndex]['net_amount'] = rate * multiplier;
  event.api.refreshCells({
    rowNodes: [event.node],
    force: true
  });

  this.updateAutoraterFooter(ladingIndex, which);
}

updateAutoraterFooter(ladingIndex: number, which: 'costs'|'chargeables'){
  let newSubtotal = 0.0;
  const tax = this.autorated_ladings_list[ladingIndex]['financials'][which]['tax'];
  const ladingFinancials = this.autorated_ladings_list[ladingIndex]['financials'][which]['line_items'];
  for (const lineItem of ladingFinancials){
    console.log(`Adding ${lineItem.net_amount ?? 0} for ${lineItem.service} to subtotal ${newSubtotal}`);
    newSubtotal += lineItem.net_amount ?? 0;
    console.log(`now subtotal is ${newSubtotal}`)
  }
  this.autorated_ladings_list[ladingIndex]['financials'][which]['subtotal'] = newSubtotal;
  this.autorated_ladings_list[ladingIndex]['financials'][which]['total'] = newSubtotal * (1 + tax);
}

  toggleLabourBreakdown(): void {
    this.is_labour_breakdown_collapsed = !this.is_labour_breakdown_collapsed;
  }

  onRateInput(event: Event, element: any): void {
    const parsed = parseFloat((event.target as HTMLInputElement).value);
    element.rate = isNaN(parsed) ? null : parsed;
  }
  onMultiplierInput(value: string, element: any): void {
    const parsed = parseFloat(value);
    element.multiplier = isNaN(parsed) ? null : parsed;
  }

  // ————————————————————————————————————————————————————————————————
  // Mirror cost handler for the “Chargeables” grid
  onChargeableCellValueChanged(ev: CellValueChangedEvent): void {
    const item = ev.data as LineItem;
    item.net_amount = (item.rate ?? 0) * (item.multiplier ?? 1);
    this.pinnedBottomRowData = this.getPinnedBottomRowData();
}

/** Utility for readonly-multiplier logic in your autorater form */
getServiceRule(serviceName: string): { rate: boolean; multiplier: boolean } | null {
return this.SERVICE_RULES[serviceName] ?? null;
}
/** Utility to split comma-separated emails and filter invalid ones */
private parseEmailList(emailString: string | null): string[] {
  // Simple regex for basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!emailString) return [];
  
  return emailString.split(',')
                    .map(email => email.trim())
                    .filter(email => emailRegex.test(email)); 
}

sendCarrierConfirmationEmail(send_as_pdf: boolean){
  let url = '/api/work_orders/carrier_rate_confirmation_emailer.php';
  // Construct the POST body with optional CC/BCC
  let postBody = {
    'lading_uuid': this.lading_uuid,
    'send_copy_to_user': this.sendCopyToggle,
    'optional_cc_emails': this.parseEmailList(this.optionalCCControl.value),
    'optional_bcc_emails': this.parseEmailList(this.optionalBCCControl.value),
    'send_as_pdf': send_as_pdf
  }
  
  this.http.post<any>(url, postBody).subscribe(
    res => {
      console.log('API Success Response:', res);
      // Check for the API response code (404 in body) to trigger the modal
      if(res.code && res.code === 404){
        this.toastr.warning('Carrier email not found. Please enter manually.');
        this.carrierEmailAddressControl.reset(); // Reset to clear any previous manual attempts
        this.pdfToggle = send_as_pdf;
        this.dialog.open(this.carrierEmailAddressDialog);
      } else if (res.success === true || res.status === 'Email sent successfully!') {
        this.toastr.success('Carrier confirmation email sent!');
        this.optionalCCControl.reset(); // Clear optional fields on success
        this.optionalBCCControl.reset();
      } else {
        // Handle other API-level errors (not a 404/modal-trigger)
        this.toastr.error(`Email failed: ${res.message || 'Unknown error'}`);
      }
    },
    err => {
      console.error('HTTP Error:', err);
      this.toastr.error('A general error occurred while trying to send the email.');
    }
  )
}

sendCarrierConfirmationEmailWithAddressInput(){
  // Validate the manual email input
  if (this.carrierEmailAddressControl.invalid) {
    this.carrierEmailAddressControl.markAsTouched();
    this.toastr.error('Please enter a valid carrier email address.');
    return;
  }
  
  let url = '/api/work_orders/carrier_rate_confirmation_emailer.php';
  let postBody = {
    'lading_uuid': this.lading_uuid,
    'carrier_email_address': this.carrierEmailAddressControl.value, // Manual email passed here
    'send_copy_to_user': this.sendCopyToggle,
    'optional_cc_emails': this.parseEmailList(this.optionalCCControl.value),
    'optional_bcc_emails': this.parseEmailList(this.optionalBCCControl.value),
    'send_as_pdf': this.pdfToggle
  }
  
  this.http.post<any>(url, postBody).subscribe(
    res => {
      console.log(res);
      if (res.success === true || res.status === 'Email sent successfully!') {
        this.toastr.success('Carrier confirmation email sent!');
        this.closeCarrierEmailAddressDialog();
      } else {
        this.toastr.error(`Email failed: ${res.message || 'Unknown error'}`);
      }
    },
    err => {
      console.error(err);
      this.toastr.error('Error sending email.');
    }
  )
}

closeCarrierEmailAddressDialog(){
  // Clear the controls and close the dialog
  this.carrierEmailAddressControl.reset();
  this.optionalCCControl.reset();
  this.optionalBCCControl.reset();
  this.dialog.closeAll();
}

}

@Pipe({ name: 'replace' })
export class ReplacePipe implements PipeTransform {
transform(value: string, search: string, replacement: string): string {
if (!value || !search || replacement === undefined) return value;
return value.split(search).join(replacement);
}
}

// --- Interfaces ---
export interface LadingData {
lading_uuid: string;
alternate_reference: string;
lading_type?: number;
financials: LadingFinancials;
warnings?: string[];
}
export interface LadingFinancials {
costs: { line_items: LineItem[]; subtotal: number; tax: number; total: number };
chargeables: { line_items: LineItem[]; subtotal: number; tax: number; total: number };
}
export interface LineItem {
service: string;
rate: number | null;
multiplier: number | null;
net_amount: number | null;
contact_service_rate_uuid: string | null;
freight_rate_uuid: string | null;
note: string | null;
lading_charge_line_item_uuid?: string | null;
charge_mode: ChargeMode
}
export type MissingDataPostBody = Record<string, Record<string, any>>;
export interface ProductBreakdownRow {
product_class: string;
product_description: string;
installation_min: string | number;
handling_min: string | number;
installation_cost: number;
installation_chargeable: number;
handling_cost: number;
handling_chargeable: number;
}
export interface TotalMinutes {
handling?: string | number;
installation?: string | number;
handling_cost?: number;
handling_chargeable?: number;
installation_cost?: number;
installation_chargeable?: number;
}
export interface ErrorResponse {
file: string;
code: string;
message: string;
}
export interface ContactProfile {
  contact_profile_uuid?: string,
  profile_name: string
}
export interface ContactProfilesResponse{
  success: boolean,
  file: string,
  code: number,
  message?: string,
  data?: ContactProfile[]
}
export type ChargeMode = "flat" | "per_unit" | "custom" | "tax" | "summary";
export interface SemiautoraterPostBody{
  lading_uuid: string,
  action: string,
  data: MissingDataPostBody,  //user-entered data required by rater
  contact_profile_uuid?: string
}